import { createFileRoute } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { Button } from '@/components/ui/button'
import { RxPlus } from 'react-icons/rx'
import { Dumbbell } from 'lucide-react'
import { useState } from 'react'
import {
  columns,
  type LibraryWorkout,
} from '@/components/workouts/workoutsColumns'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import NewWorkoutDialog from '@/components/workouts/newWorkoutDialog'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import DeleteAlert from '@/components/DeleteAlert'
import { trpc } from '@/lib/trpc/client'
import { useOrg } from '@/lib/states/session/hooks'
import { errorToast } from '@/lib/utils/ToastUtils'
import { patchListCache, removeFromList } from '@trainrr/api-client'

export const Route = createFileRoute('/_auth/_dashboard/workouts/')({
  beforeLoad: () => {
    requirePermission('workouts.write')
  },
  component: WorkoutList,
})

function WorkoutList() {
  const navigate = Route.useNavigate()
  const org = useOrg()
  const [open, setOpen] = useState(false)
  const [workoutToDelete, setWorkoutToDelete] = useState<LibraryWorkout>()
  const utils = trpc.useUtils()
  const { data: workouts, isLoading } = trpc.workouts.list.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })
  const deleteWorkout = trpc.workouts.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.workouts.list, (old) =>
        removeFromList(old, variables.workoutId),
      )
      setWorkoutToDelete(undefined)
    },
    onError: (error) => {
      console.error('Failed to delete workout', error)
      errorToast(error)
    },
  })

  const handleOnClickEdit = (workout: LibraryWorkout) => {
    navigate({ to: '/workouts/$workoutId', params: { workoutId: workout.id } })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
  }

  const handleNewWorkoutCreated = (id: string) => {
    navigate({ to: '/workouts/$workoutId', params: { workoutId: id } })
    setOpen(false)
  }

  return (
    <>
      <DeleteAlert
        title="Delete workout"
        description="Are you sure you want to delete this workout? This action cannot be undone."
        open={workoutToDelete !== undefined}
        onCancel={() => setWorkoutToDelete(undefined)}
        onDelete={() => {
          if (!workoutToDelete) {
            return
          }
          deleteWorkout.mutate({ workoutId: workoutToDelete.id })
        }}
      />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Workouts</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={columns}
              data={workouts ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={setWorkoutToDelete}
              showSearchBar
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
              emptyView={
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Dumbbell className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyTitle>No workouts yet</EmptyTitle>
                    <EmptyDescription>
                      Build your first workout template to reuse across client
                      plans.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button className="gap-2" onClick={() => setOpen(true)}>
                      <RxPlus /> Create workout
                    </Button>
                  </EmptyContent>
                </Empty>
              }
            />
          </div>
        </div>
      </div>
      <NewWorkoutDialog
        open={open}
        onOpenChange={handleOpenChange}
        onWorkoutCreated={handleNewWorkoutCreated}
      />
    </>
  )
}
